import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  bulkProductsApi,
  type BulkExportQuery,
  type DownloadedFile,
} from "../api/productImport";
import type { BulkCommitResponse, BulkPreview } from "../api/types";
import { saveFile } from "./labels";

/**
 * The three things this screen does, and nothing it does on its own.
 *
 * Every one of them is a MUTATION in the react-query sense — including
 * validation, which writes nothing but must never run because a component
 * rendered. A query would refetch on focus, on reconnect and on remount, and
 * an operator returning to this tab would watch a file they had already
 * confirmed be re-checked behind them.
 *
 * NOTHING AUTO-COMMITS. Choosing a file validates; confirming is a separate
 * act with its own button and its own mutation.
 */

export function useValidateWorkbook() {
  return useMutation<BulkPreview, unknown, File>({
    mutationFn: (file) => bulkProductsApi.validate(file),
  });
}

export function useCommitWorkbook() {
  const queryClient = useQueryClient();
  return useMutation<BulkCommitResponse, unknown,
                     { file: File; planDigest: string }>({
    mutationFn: ({ file, planDigest }) =>
      bulkProductsApi.commit(file, planDigest),
    onSuccess: async () => {
      // Products and inventory both moved. Invalidating rather than patching:
      // this screen does not know what the drafts it created look like, and
      // guessing would put a made-up product in another screen's cache.
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useDownloadTemplate() {
  return useMutation<DownloadedFile, unknown, void>({
    mutationFn: () => bulkProductsApi.template(),
    onSuccess: (file) => saveFile(file.blob, file.filename),
  });
}

export function useExportProducts() {
  return useMutation<DownloadedFile, unknown, BulkExportQuery>({
    mutationFn: (query) => bulkProductsApi.export(query),
    onSuccess: (file) => saveFile(file.blob, file.filename),
  });
}
